/* eslint-disable @repo/internal/react/no-class-components */

/* There is no good way to replace ErrorBoundary class components with a
 * functional component; currently no functional hook that can replace
 * 'componentDidCatch'.
 * Source: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 *
 * But I needed to get 'createAnalyticsEvent' via a hook hence
 * the wrapped class component.
 */

import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { logException } from '@atlaskit/editor-common/monitoring';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import { UnhandledErrorScreen } from '@atlassian/generative-ai-modal/screens/UnhandledError';

import type { MarkdownConversionError } from '../../../types/MarkdownConversionError';
import { markdownConversionErrorName } from '../../../types/MarkdownConversionError';
import { useFireAIAnalyticsEvent } from '../../utils/analytics/utils';

/**
 *
 * Refactored to use react-error-boundary so we don't have to maintain our own.
 * The package is already used elsewhere.
 * Currently gated behind platform_editor_ai_aifc too.
 */
export const ModalRegionErrorBoundary = (props: {
	children: React.ReactNode;
	inContentComponent?: boolean;
	onCloseFallback: () => void;
}): React.JSX.Element => {
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const { formatMessage } = useIntl();

	return (
		<ExperienceApplicationErrorBoundaryClass
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...props}
			fireAIAnalyticsEvent={fireAIAnalyticsEvent}
			formatMessage={formatMessage}
		/>
	);
};

interface ErrorBoundaryProps {
	children: React.ReactNode;
	fireAIAnalyticsEvent: ReturnType<typeof useFireAIAnalyticsEvent>;
	formatMessage: IntlShape['formatMessage'];
	onCloseFallback: () => void;
}

interface ErrorBoundaryState {
	errorCaptured: boolean;
	fallbackMessage?: string;
}

class ExperienceApplicationErrorBoundaryClass extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	private getFallbackMessage = (descriptor: MessageDescriptor) => {
		return this.props.formatMessage(descriptor);
	};

	state = {
		errorCaptured: false,
		fallbackMessage: this.getFallbackMessage(aiMessages.unhandledErrorMessage),
	};

	private shouldRecover() {
		return this.state.errorCaptured;
	}

	componentDidCatch(error: Error | MarkdownConversionError, errorInfo: React.ErrorInfo) {
		logException(error, { location: 'editor-plugin-ai-error-boundary' });
		if (error.name === markdownConversionErrorName) {
			const errorHasTypeProperty = 'type' in error;
			const errorSubType = errorHasTypeProperty ? error.type : undefined;

			this.props.fireAIAnalyticsEvent({
				payload: {
					action: 'unhandledErrorCaught',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'experienceApplication',
					attributes: {
						componentStack: errorInfo.componentStack || '',
						errorType: 'markdownToProseMirrorError',
						errorSubType,
						errorMessage: ErrorUtils.extractErrorMessage(error),
					},
					eventType: EVENT_TYPE.OPERATIONAL,
				},
			});
			this.setState({
				errorCaptured: true,
				fallbackMessage: this.getFallbackMessage(aiMessages.markdownErrorMessage),
			});
		} else {
			this.props.fireAIAnalyticsEvent({
				payload: {
					action: 'unhandledErrorCaught',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'experienceApplication',
					attributes: {
						componentStack: errorInfo.componentStack || '',
						errorMessage: ErrorUtils.extractErrorMessage(error),
					},
					eventType: EVENT_TYPE.OPERATIONAL,
				},
			});
			this.setState({
				errorCaptured: true,
				fallbackMessage: this.getFallbackMessage(aiMessages.unhandledErrorMessage),
			});
		}
	}

	render() {
		if (this.shouldRecover()) {
			return (
				<UnhandledErrorScreen
					text={this.state.fallbackMessage}
					onCancel={this.props.onCloseFallback}
				/>
			);
		}
		return this.props.children;
	}
}
